/* eslint-disable react/prop-types */
import React, { Component } from 'react';
import axios from 'axios';
import { Redirect, Link } from 'react-router-dom';
import withStyles from '@material-ui/core/styles/withStyles';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import { Alert } from '@material-ui/lab';
import Grid from '@material-ui/core/Grid';

import NavigationBar from 'components/inc/NavigationBar.jsx';
import Card from 'components/Card/Card.jsx';
import CardBody from 'components/Card/CardBody.jsx';
import CardHeader from 'components/Card/CardHeader.jsx';
import CardFooter from 'components/Card/CardFooter';
import Footer from 'components/Footer/Footer.jsx';

import { API_END_POINTS } from 'utils/constants';

const API_ERROR_MESSAGE_ALREADY_VERIFIED = 'email link already verified';
const API_ERROR_MESSAGE_LINK_EXPIRED = 'email link is expired or invalid';
const API_ERROR_MESSAGE_PASSWORD_INVALID =
  'Password must be atleast 6 characters containing atleast 1 number, 1 specialcase, 1 lowercase, and 1 upercase.';

const styles = {
  gridContainer: { minHeight: '80vh' },
  textCenter: {
    textAlign: 'center',
  },
  card: {
    marginTop: '2rem',
  },
  cardHeader: {
    textAlign: 'center',
    marginBottom: '1.5rem',
  },
  cardFooter: {
    justifyContent: 'center',
    marginTop: '1rem',
  },
};

const FormMessageDisplay = ({ errorMessage }) => {
  if (errorMessage === API_ERROR_MESSAGE_ALREADY_VERIFIED) {
    return (
      <Alert severity='info'>
        It looks like you have already used this link to set a password.{' '}
        <Link to='/login-page'>Go to login</Link>
      </Alert>
    );
  }

  if (errorMessage === API_ERROR_MESSAGE_LINK_EXPIRED) {
    return (
      <Alert severity='warning'>
        It looks like this link is expired, please request your administrator to
        resend the email to set password.
      </Alert>
    );
  }

  if (errorMessage === API_ERROR_MESSAGE_PASSWORD_INVALID) {
    return (
      <Alert severity='error'>
        Password must be at least 6 characters containing at least 1 number, 1
        special character, 1 lowercase, and 1 uppercase alphabet.
      </Alert>
    );
  }

  return <Alert severity='error'>{errorMessage}</Alert>;
};

export class SetNewPassword extends Component {
  state = {
    newPassword: '',
    isPasswordHidden: true,
    isLoading: {
      settingPassword: false,
    },
    errors: {},
    success: {},
    redirectTo: {
      login: false,
      profile: false,
    },
    shouldRedirectToLogin: false,
  };

  componentDidMount() {
    this.checkIfOneTimeAccessCodeIsValid();
    localStorage.clear();
  }

  checkIfOneTimeAccessCodeIsValid() {
    let redirectToLogin = true;

    if (this.props.location && this.props.location.search) {
      const urlQueryParams = new URLSearchParams(this.props.location.search);

      const oneTimeAccessCode = urlQueryParams.get('code');
      const validationToken = urlQueryParams.get('validationToken');

      if (
        oneTimeAccessCode === null ||
        oneTimeAccessCode.length === 0 ||
        validationToken === null ||
        validationToken.length === 0
      ) {
        redirectToLogin = true;
      } else {
        redirectToLogin = false;
      }
    }

    if (redirectToLogin) {
      this.setState({
        redirectTo: { login: true, profile: false },
      });
    }
  }

  onInputChange = (event) => {
    event.preventDefault();

    const {
      target: { value, name },
    } = event;
    this.setState({ [name]: value, errors: {} });
  };

  togglePasswordVisibility = (e) => {
    e.preventDefault();

    this.setState({ isPasswordHidden: !this.state.isPasswordHidden });
  };

  isFormValid() {
    let errors = {};

    const { newPassword } = this.state;

    if (newPassword.trim().length === 0) {
      errors = { ...errors, newPassword: 'Please enter a valid password' };
    }

    if (Object.keys(errors).length === 0 && errors.constructor === Object) {
      return true;
    } else {
      this.setState({
        errors,
      });
      return false;
    }
  }

  async runPasswordSetApiToGetUserEmail(
    oneTimeAccessCode,
    validationToken,
    newPassword
  ) {
    const { BASE_URL, SET_PASSWORD_URL } = API_END_POINTS;

    try {
      const response = await axios({
        method: 'put',
        url: `${BASE_URL}/${SET_PASSWORD_URL}`,
        data: {
          code: oneTimeAccessCode,
          validationToken,
          password: newPassword,
        },
      });

      if (
        response &&
        response.data &&
        response.data.data &&
        response.data.data.email &&
        response.data.data.email.length !== 0
      ) {
        return response.data.data.email;
      } else {
        return '';
      }
    } catch (err) {
      console.error(err);
      let form = '';

      if (err.response) {
        const {
          data: { errorMessage },
        } = err.response;
        form = errorMessage;
      } else {
        form = `${
          err.message
        } : Oops Sorry, we are informed and will look into it`;
        console.error('Err in set password', err.request, err.message);
      }

      this.setState({
        errors: { ...this.state.errors, form },
      });

      return null;
    }
  }

  async runLoginApiToGetUserToken(email, password) {
    const { BASE_URL, USER_LOGIN_URl } = API_END_POINTS;
    try {
      let loginData = new URLSearchParams();
      loginData.append('username', email);
      loginData.append('password', password);
      loginData.append('grant_type', 'password');

      const response = await axios({
        method: 'post',
        url: `${BASE_URL}/${USER_LOGIN_URl}`,
        data: loginData,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: 'Basic YXBwbGljYXRpb246c2VjcmV0',
        },
      });

      if (
        response &&
        response.data &&
        response.data &&
        response.data.user &&
        response.data.accessToken &&
        response.data.accessToken.length !== 0 &&
        response.data.accessTokenExpiresAt &&
        response.data.accessTokenExpiresAt.length !== 0
      ) {
        return {
          accessToken: response.data.accessToken,
          accessTokenExpiry: response.data.accessTokenExpiresAt,
          userDetails: JSON.stringify(response.data.user),
          userID: response.data.user._id,
        };
      } else {
        return {
          accessToken: '',
          accessTokenExpiry: '',
          userDetails: '',
          userID: '',
        };
      }
    } catch (err) {
      console.error(err);
      localStorage.clear();
      return null;
    }
  }

  onFormSubmitForSettingPassword = async (event) => {
    event.preventDefault();

    if (this.isFormValid()) {
      const {
        location: { search },
      } = this.props;
      const { newPassword } = this.state;

      const urlQueryParams = new URLSearchParams(search);
      const oneTimeAccessCode = urlQueryParams.get('code');
      const validationToken = urlQueryParams.get('validationToken');

      // Check for query params in url once again
      if (
        oneTimeAccessCode === null ||
        oneTimeAccessCode.length === 0 ||
        validationToken === null ||
        validationToken.length === 0
      ) {
        this.setState({
          redirectTo: { login: true, profile: false },
        });
        return;
      }

      this.setState({
        isLoading: { ...this.state.isLoading, settingPassword: true },
        isPasswordHidden: true,
      });

      const userEmail = await this.runPasswordSetApiToGetUserEmail(
        oneTimeAccessCode,
        validationToken,
        newPassword
      );

      // Error during setting password
      if (userEmail === null) {
        this.setState({
          isLoading: { ...this.state.isLoading, settingPassword: false },
        });
        return;
      }

      // When user email is not defined in the response
      if (userEmail === '') {
        this.setState({
          isLoading: { ...this.state.isLoading, settingPassword: false },
          redirectTo: { login: true, profile: false },
        });
        return;
      }

      const loggedInUser = await this.runLoginApiToGetUserToken(
        userEmail,
        newPassword
      );

      // Error during logging user
      if (loggedInUser === null) {
        this.setState({
          isLoading: { ...this.state.isLoading, settingPassword: false },
          redirectTo: { login: true, profile: false },
        });
        return;
      }

      // When user details comes out empty
      if (
        loggedInUser.accessToken === '' ||
        loggedInUser.accessTokenExpiry === '' ||
        loggedInUser.userDetails === '' ||
        loggedInUser.userID === ''
      ) {
        this.setState({
          isLoading: { ...this.state.isLoading, settingPassword: false },
          redirectTo: { login: true, profile: false },
        });
        return;
      }

      // When everything is fine with setting password and logging user
      localStorage.setItem('accessToken', loggedInUser.accessToken);
      localStorage.setItem('expireToken', loggedInUser.accessTokenExpiry);
      localStorage.setItem('userID', loggedInUser.userID);
      localStorage.setItem('userData', loggedInUser.userDetails);

      this.setState({
        isLoading: { ...this.state.isLoading, settingPassword: false },
        redirectTo: { login: false, profile: true },
      });
    }
  };

  render() {
    const { classes } = this.props;
    const {
      newPassword,
      isPasswordHidden,
      isLoading,
      errors,
      redirectTo,
    } = this.state;

    if (redirectTo['login']) {
      return <Redirect to={'/login-page'} />;
    }

    if (redirectTo['profile']) {
      return <Redirect to='profile-page' />;
    }

    return (
      <main>
        <NavigationBar />
        <Grid
          container
          classes={{ root: classes.gridContainer }}
          justify='center'
          alignItems='center'
        >
          <Grid item xs={12} sm={12} md={5} lg={4}>
            <Card>
              <form onSubmit={this.onFormSubmitForSettingPassword}>
                <CardHeader
                  color='success'
                  login
                  className={classes.cardHeader}
                >
                  <h4>Set a password to your newly created account</h4>
                </CardHeader>
                <CardBody signup>
                  {errors['form'] && errors['form'] !== '' && (
                    <FormMessageDisplay errorMessage={errors['form']} />
                  )}
                  <TextField
                    id='set-new-password-field'
                    label='New password'
                    placeholder='Enter a new password'
                    fullWidth
                    margin='normal'
                    type={isPasswordHidden ? 'password' : 'text'}
                    helperText={
                      errors['newPassword'] && errors['newPassword'] !== ''
                        ? errors['newPassword']
                        : ''
                    }
                    error={
                      errors['newPassword'] && errors['newPassword'] !== ''
                    }
                    value={newPassword}
                    name='newPassword'
                    onChange={this.onInputChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            aria-label='toggle password visibility'
                            onClick={this.togglePasswordVisibility}
                          >
                            {isPasswordHidden ? (
                              <VisibilityIcon />
                            ) : (
                              <VisibilityOffIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                      readOnly: isLoading['settingPassword'] ? true : false,
                      autoFocus: true,
                    }}
                  />
                </CardBody>
                <CardFooter className={classes.cardFooter}>
                  <Button color='primary' size='large' type='submit'>
                    {isLoading['settingPassword'] ? (
                      <CircularProgress size={30} />
                    ) : (
                      'Set password'
                    )}
                  </Button>
                </CardFooter>
              </form>
            </Card>
          </Grid>
        </Grid>
        <Footer
          theme='#e5e5e5'
          content={
            <div>
              <div className={classes.right}>
                A &copy;&nbsp;
                <a href='http://www.root-nyc.com'>Root NYC</a>
                &nbsp;Product
              </div>
            </div>
          }
        />
      </main>
    );
  }
}

export default withStyles(styles)(SetNewPassword);
